import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import {
  Trophy,
  Timer,
  Zap,
  Target,
  Activity,
  Medal,
  ChevronRight,
  BadgeCheck,
  TrendingUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// NSB Logo Component
import { NSBLogo } from "../components/NSBLogo";

// Metric config
const METRIC_CONFIG = {
  sixty_time: {
    name: "60-Yard Dash",
    icon: Timer,
    unit: "sec",
    color: "from-blue-500 to-cyan-500",
    description: "Fastest 60-yard times"
  },
  exit_velocity: {
    name: "Exit Velocity",
    icon: Zap,
    unit: "mph",
    color: "from-orange-500 to-red-500",
    description: "Highest exit velocities"
  },
  fastball_velocity: {
    name: "Fastball Velocity",
    icon: Target,
    unit: "mph",
    color: "from-blue-800 to-pink-500",
    description: "Hardest throwing pitchers"
  },
  bat_speed: {
    name: "Bat Speed",
    icon: Activity,
    unit: "mph",
    color: "from-green-500 to-emerald-500",
    description: "Fastest bat speeds"
  },
  pop_time: {
    name: "Pop Time",
    icon: Timer,
    unit: "sec",
    color: "from-indigo-500 to-blue-500",
    description: "Quickest catcher pop times"
  },
  gpa: {
    name: "GPA",
    icon: Trophy,
    unit: "",
    color: "from-yellow-500 to-amber-500",
    description: "Highest academic achievers"
  }
};

// Rank Badge Component
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
        <Medal className="h-5 w-5 text-white" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
        <span className="text-sm font-bold text-white">{rank}</span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-600 to-amber-700 flex items-center justify-center">
        <span className="text-sm font-bold text-white">{rank}</span>
      </div>
    );
  }
  return (
    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
      <span className="text-sm font-bold text-gray-600">{rank}</span>
    </div>
  );
};

// Leaderboard Card Component
const LeaderboardCard = ({ metricType, leaderboard, loading }) => {
  const config = METRIC_CONFIG[metricType];
  const Icon = config?.icon || Trophy;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className={`bg-gradient-to-r ${config?.color || 'from-blue-500 to-blue-800'} p-4 text-white`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold">{config?.name || metricType}</h3>
            <p className="text-sm text-white/80">{config?.description}</p>
          </div>
        </div>
      </div>
      
      <div className="divide-y divide-gray-100">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto"></div>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Trophy className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>No data available</p>
          </div>
        ) : (
          leaderboard.slice(0, 10).map((entry) => (
            <Link 
              key={entry.athlete_id}
              to={`/athlete/${entry.athlete_id}`}
              className="flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors"
            >
              <RankBadge rank={entry.rank} />
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-900 flex items-center justify-center text-white font-bold flex-shrink-0">
                {entry.name?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 truncate">{entry.name}</span>
                  {entry.is_verified && <BadgeCheck className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {entry.position} • {entry.school} • {entry.graduation_year}
                </div>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold text-gray-900">{entry.metric_value}</span>
                {config?.unit && <span className="text-sm text-gray-500 ml-1">{config.unit}</span>}
              </div>
              <ChevronRight className="h-4 w-4 text-gray-300" />
            </Link>
          ))
        )}
      </div>
    </div>
  );
};

export default function LeaderboardPage() {
  const [selectedMetric, setSelectedMetric] = useState("sixty_time");
  const [graduationYear, setGraduationYear] = useState("all");
  const [position, setPosition] = useState("all");
  const [leaderboard, setLeaderboard] = useState([]);
  const [allLeaderboards, setAllLeaderboards] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("all"); // "all" or "single"

  useEffect(() => {
    if (viewMode === "all") {
      fetchAllLeaderboards();
    } else {
      fetchLeaderboard();
    }
  }, [selectedMetric, graduationYear, position, viewMode]);

  const fetchAllLeaderboards = async () => {
    setLoading(true);
    try {
      const yearParam = graduationYear !== "all" ? `?graduation_year=${graduationYear}` : "";
      const res = await fetch(`${API_URL}/api/leaderboards/all${yearParam}`);
      if (res.ok) {
        const data = await res.json();
        setAllLeaderboards(data.leaderboards || {});
      }
    } catch (error) {
      console.error("Error fetching leaderboards:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/leaderboard/${selectedMetric}?limit=25`;
      if (graduationYear !== "all") url += `&graduation_year=${graduationYear}`;
      if (position !== "all") url += `&position=${position}`;
      
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const gradYears = [currentYear, currentYear + 1, currentYear + 2, currentYear + 3, currentYear + 4];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="leaderboard-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <NSBLogo className="h-10" light={false} />
            </Link>
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Leaderboards</h1>
              <p className="text-sm text-gray-500">Top athletes by verified metrics</p>
            </div>
          </div>
          <Link to="/login">
            <Button variant="outline">Sign In</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-4">
            <Trophy className="h-5 w-5 text-yellow-400" />
            <span className="text-sm font-medium">NSB Verified Rankings</span>
          </div>
          <h2 className="text-4xl font-bold mb-4">Athlete Leaderboards</h2>
          <p className="text-blue-200 max-w-2xl mx-auto">
            Compare athletes across key metrics. All data is verified through official NSB combines and evaluations.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">View:</span>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("all")}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "all" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  All Metrics
                </button>
                <button
                  onClick={() => setViewMode("single")}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "single" ? "bg-white shadow text-gray-900" : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  Single Metric
                </button>
              </div>
            </div>

            {viewMode === "single" && (
              <Select value={selectedMetric} onValueChange={setSelectedMetric}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select metric" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(METRIC_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={graduationYear} onValueChange={setGraduationYear}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Grad Year" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Years</SelectItem>
                {gradYears.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {viewMode === "single" && (
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Position" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Positions</SelectItem>
                  <SelectItem value="SS">SS</SelectItem>
                  <SelectItem value="2B">2B</SelectItem>
                  <SelectItem value="3B">3B</SelectItem>
                  <SelectItem value="1B">1B</SelectItem>
                  <SelectItem value="C">C</SelectItem>
                  <SelectItem value="CF">CF</SelectItem>
                  <SelectItem value="LF">LF</SelectItem>
                  <SelectItem value="RF">RF</SelectItem>
                  <SelectItem value="P">P</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Leaderboards */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {viewMode === "all" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(METRIC_CONFIG).slice(0, 4).map(([metric, config]) => (
              <LeaderboardCard
                key={metric}
                metricType={metric}
                leaderboard={allLeaderboards[metric] || []}
                loading={loading}
              />
            ))}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <LeaderboardCard
              metricType={selectedMetric}
              leaderboard={leaderboard}
              loading={loading}
            />
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-900 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Want to see your name on the leaderboard?</h3>
          <p className="text-blue-100 mb-6">
            Get verified at an official NSB combine and showcase your talents to college coaches nationwide.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
                Create Profile
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              Find a Combine
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <NSBLogo className="h-8 mx-auto mb-4" light={false} />
          <p className="text-sm text-gray-500">© 2026 National Scouting Bureau. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
